import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { useLogin, useSession } from '@/features/auth/useSession'

export function LoginPage() {
  const { t } = useTranslation()
  const session = useSession()
  const loginMutation = useLogin()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  if (session.data) {
    return <Navigate to="/portfolio" replace />
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    loginMutation.mutate({ email, password })
  }

  // The API returns 422 with field errors; anything else is a connection problem.
  const fieldError = loginMutation.error?.errors?.email?.[0]
  const generalError =
    loginMutation.error && !fieldError ? t('auth.unreachable') : undefined

  return (
    <div className="grid min-h-dvh place-items-center px-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          <img src="/awj-systems-logo-v.svg" alt="" className="h-8 w-auto" />
          <div className="leading-tight">
            <div className="text-ink-900 font-semibold">{t('app.name')}</div>
            <div className="text-ink-500 text-xs">{t('app.org')}</div>
          </div>
        </div>

        <form onSubmit={submit} className="border-line bg-surface rounded-lg border p-6">
          <h1 className="text-ink-900 mb-5 text-lg font-semibold">{t('auth.signIn')}</h1>

          <label className="mb-4 block">
            <span className="text-ink-700 mb-1.5 block text-sm font-medium">
              {t('auth.email')}
            </span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoComplete="username"
              className="border-line focus:border-brand-600 w-full rounded-md border px-3 py-2 text-sm outline-none"
            />
          </label>

          <label className="mb-5 block">
            <span className="text-ink-700 mb-1.5 block text-sm font-medium">
              {t('auth.password')}
            </span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              autoComplete="current-password"
              className="border-line focus:border-brand-600 w-full rounded-md border px-3 py-2 text-sm outline-none"
            />
          </label>

          {fieldError ? (
            <p className="mb-4 text-sm text-[color:var(--color-state-blocked)]">{fieldError}</p>
          ) : null}
          {generalError ? (
            <p className="mb-4 text-sm text-[color:var(--color-state-blocked)]">{generalError}</p>
          ) : null}

          <button
            type="submit"
            disabled={loginMutation.isPending}
            className="bg-brand-600 hover:bg-brand-700 w-full rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {loginMutation.isPending ? t('auth.signingIn') : t('auth.signIn')}
          </button>
        </form>

        <p className="text-ink-500 mt-4 text-center text-xs">{t('auth.guestHint')}</p>
      </div>
    </div>
  )
}
